/**
 * Which purchase flow the client is currently in.
 *
 * Both flows send the same event NAMES, on purpose — that is what puts v2 and
 * legacy on one comparable chart — so something has to tell them apart, and
 * that something is a `version` attribute that only v2 sets. Legacy is never
 * changed: an event with no `version` IS a legacy event. Note the attribute is
 * then ABSENT rather than null, so a segment must be built on "is not set", not
 * on `!= 2`.
 *
 * It lives here, read by `logAnalyticsEvent` itself, rather than being stamped
 * at each purchase-flow call site, because a good half of what a client does in
 * v2 happens inside SHARED components — the preferred-payment switch, the upload
 * widgets, the staging popups — which fire their own events and know nothing
 * about which flow is hosting them. Stamped per call site, those events go out
 * unmarked and are counted as legacy traffic while the client is demonstrably in
 * v2, which is worse than not measuring them: it moves v2's numbers onto
 * legacy's line.
 *
 * The flow shell sets this on mount and clears it on unmount. Purchase flow v2
 * is a whole-page route, so while it is set there is no other page mounted to
 * mislabel.
 */

let activeFlowVersion: number | null = null

/** Called by the flow shell. `null` clears it. */
export const setPfFlowVersion = (version: number | null): void => {
  activeFlowVersion = version
}

export const currentPfFlowVersion = (): number | null => activeFlowVersion

/** An embed load failure from before a flow was picked belongs to neither, and an absent version would make it legacy's. */
export const NO_FLOW_VERSION = 0

/**
 * Adds `version` to an event body while a versioned flow is active.
 *
 * A body that already carries its own `version` is left alone — the caller was
 * explicit, and silently overwriting it would make the one field nobody can
 * verify from the outside also the one field a call site cannot control.
 */
export const withPfFlowVersion = <T extends object>(body: T): T => {
  if (activeFlowVersion === null) return body
  if ('version' in body) return body
  return { ...body, version: activeFlowVersion }
}
